/**
 * /customers — Customer list with rich filters, sorting, multi-select bulk
 * actions, and saved segments (Phase 5.8i / Customers P4).
 * Sidebar/dialog/table pieces live in components/customers/.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Plus, Search, Users2, X, Filter, Bookmark, Tag as TagIcon,
} from "lucide-react";
import { usePageExport } from "@/context/ExportContext";
import { useAuth } from "@/context/AuthContext";
import TagEditor from "@/components/customers/TagEditor";
import { SegmentsSidebar } from "@/components/customers/SegmentsSidebar";
import { AddCustomerDialog } from "@/components/customers/AddCustomerDialog";
import { BulkTagDialog } from "@/components/customers/BulkTagDialog";
import { FilterTag, TriToggle, SortHead, TagsCell } from "@/components/customers/ContactsTableBits";

const DEFAULT_FILTERS = {
  q: "",
  tags: [],
  language: "all",
  has_email: "any",
  has_open_tickets: "any",
  created_from: "",
  created_to: "",
  sort: "created_at",
  dir: "desc",
  segment_id: "",
};

const PAGE_SIZE = 50;

export default function Contacts() {
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canBulk = hasPerm("customers.edit_details");

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    q: searchParams.get("q") || "",
    segment_id: searchParams.get("segment") || "",
  }));
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const [segments, setSegments] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkTagDialog, setBulkTagDialog] = useState(null);  // 'add' | 'remove'

  // Country codes lookup (for phone-length validation)
  const [countryCodes, setCountryCodes] = useState([]);

  // ---------- Load -----------
  const debounceRef = useRef(null);
  const load = useCallback(() => {
    setLoading(true);
    const params = {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      sort: filters.sort, dir: filters.dir,
    };
    if (filters.q) params.q = filters.q;
    if (filters.tags.length) params.tags = filters.tags.join(",");
    if (filters.language !== "all") params.language = filters.language;
    if (filters.has_email !== "any") params.has_email = filters.has_email === "yes";
    if (filters.has_open_tickets !== "any") params.has_open_tickets = filters.has_open_tickets === "yes";
    if (filters.created_from) params.created_from = filters.created_from;
    if (filters.created_to) params.created_to = filters.created_to;
    api.get("/contacts", { params })
      .then((r) => {
        setContacts(r.data.rows || []);
        setTotal(r.data.total || 0);
        setSelected(new Set());
      })
      .finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [load]);

  // Reset to first page when filters change (but not when page itself changes)
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [
    filters.q, filters.tags.join(","), filters.language, filters.has_email,
    filters.has_open_tickets, filters.created_from, filters.created_to, filters.sort, filters.dir,
  ]);

  // Load segments
  const loadSegments = useCallback(() => {
    api.get("/customer-segments").then((r) => setSegments(r.data || []));
  }, []);
  useEffect(() => { loadSegments(); }, [loadSegments]);

  // Country codes (for phone-length validation on the add form)
  useEffect(() => {
    api.get("/country-codes").then((r) => setCountryCodes(r.data || [])).catch(() => {});
  }, []);

  // ---------- CSV export ----------
  const exportColumns = useMemo(() => [
    { key: "name", header: "Name" },
    { key: "language", header: "Language" },
    { key: "phone_e164", header: "Phone (E.164)" },
    { key: "alt_phone", header: "Alt Phone" },
    { key: "email", header: "Email" },
    { key: "tags", header: "Tags" },
    { key: "ticket_count", header: "Tickets" },
    { key: "open_count", header: "Open" },
    { key: "csat_avg", header: "CSAT" },
    { key: "last_ticket_at", header: "Last Contact" },
    { key: "created_at", header: "Created" },
  ], []);
  const exportRows = useMemo(() => contacts.map((c) => ({
    ...c, tags: (c.tags || []).join(", "),
  })), [contacts]);
  usePageExport({ filename: "kippu-customers.csv", rows: exportRows, columns: exportColumns, label: "Customers" });

  // ---------- Actions ----------
  const toggleSort = (col) => {
    setFilters((f) => f.sort === col
      ? { ...f, dir: f.dir === "asc" ? "desc" : "asc" }
      : { ...f, sort: col, dir: col === "name" ? "asc" : "desc" });
  };

  const applySegment = (seg) => {
    const cfg = seg.filter_config || {};
    setFilters((f) => ({
      ...DEFAULT_FILTERS,
      segment_id: seg.id,
      tags: cfg.tags || [],
      language: cfg.language || "all",
      has_email: cfg.has_email ?? "any",
      has_open_tickets: cfg.has_open_tickets ?? "any",
      q: cfg.q || "",
      sort: cfg.sort || "created_at",
      dir: cfg.dir || "desc",
    }));
    setSearchParams({ segment: seg.id });
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams({});
  };

  const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ---------- Render ----------
  const activeSegment = segments.find((s) => s.id === filters.segment_id);
  const hasActiveFilters = filters.q || filters.tags.length || filters.language !== "all"
    || filters.has_email !== "any" || filters.has_open_tickets !== "any"
    || filters.created_from || filters.created_to;

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        <SegmentsSidebar
          segments={segments}
          user={user}
          canBulk={canBulk}
          filters={filters}
          activeSegmentId={filters.segment_id}
          onApplySegment={applySegment}
          onClearFilters={clearFilters}
          reloadSegments={loadSegments}
        />

        {/* Main pane */}
        <div className="space-y-4 min-w-0">
          {/* Toolbar */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                         placeholder="Search by name, phone, email…" className="pl-9 h-9 text-sm"
                         data-testid="customers-search" />
                </div>
                <Button asChild variant="outline" data-testid="open-duplicates-btn" className="h-9">
                  <Link to="/customers/duplicates">
                    <Users2 className="w-3.5 h-3.5 mr-1.5" /> Find duplicates
                  </Link>
                </Button>
                <Button onClick={() => setAddOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 h-9"
                        data-testid="add-customer-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add customer
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2.5">
                <FilterTag label="Language">
                  <Select value={filters.language} onValueChange={(v) => setFilters({ ...filters, language: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="filter-language"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">All</SelectItem>
                      <SelectItem value="English" className="text-xs">English</SelectItem>
                      <SelectItem value="Arabic" className="text-xs">Arabic</SelectItem>
                      <SelectItem value="Hindi" className="text-xs">Hindi</SelectItem>
                      <SelectItem value="Urdu" className="text-xs">Urdu</SelectItem>
                    </SelectContent>
                  </Select>
                </FilterTag>
                <FilterTag label="Has email">
                  <TriToggle value={filters.has_email} onChange={(v) => setFilters({ ...filters, has_email: v })} testid="filter-has-email" />
                </FilterTag>
                <FilterTag label="Open tickets">
                  <TriToggle value={filters.has_open_tickets} onChange={(v) => setFilters({ ...filters, has_open_tickets: v })} testid="filter-has-open" />
                </FilterTag>
                <FilterTag label="Created from">
                  <Input type="date" value={filters.created_from}
                         onChange={(e) => setFilters({ ...filters, created_from: e.target.value })}
                         className="h-9 text-xs" data-testid="filter-created-from" />
                </FilterTag>
                <FilterTag label="Created to">
                  <Input type="date" value={filters.created_to}
                         onChange={(e) => setFilters({ ...filters, created_to: e.target.value })}
                         className="h-9 text-xs" data-testid="filter-created-to" />
                </FilterTag>
              </div>
              <div className="flex items-start gap-2 flex-wrap">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2 shrink-0">Tags</Label>
                <div className="flex-1 min-w-[200px]">
                  <TagEditor value={filters.tags} onChange={(v) => setFilters({ ...filters, tags: v })}
                             placeholder="Filter by tag (any match)…" />
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="filters-reset"
                          className="text-slate-500 mt-1">
                    <Filter className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
              {activeSegment && (
                <div className="text-[11px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-3 py-1.5 inline-flex items-center gap-2">
                  <Bookmark className="w-3 h-3" />
                  Applied segment: <b>{activeSegment.name}</b>
                  {activeSegment.share_scope === "org" && <Badge variant="outline" className="bg-white text-[10px]">org</Badge>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bulk action bar */}
          {selected.size > 0 && canBulk && (
            <div className="sticky top-16 z-20 bg-slate-900 text-white rounded-md p-2.5 px-4 flex items-center gap-3 flex-wrap"
                 data-testid="bulk-action-bar">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-white hover:bg-slate-800 h-8 text-xs"
                      onClick={() => setBulkTagDialog("add")}
                      data-testid="bulk-add-tag-btn">
                <TagIcon className="w-3.5 h-3.5 mr-1" /> Add tag
              </Button>
              <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-white hover:bg-slate-800 h-8 text-xs"
                      onClick={() => setBulkTagDialog("remove")}
                      data-testid="bulk-remove-tag-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Remove tag
              </Button>
              <button onClick={() => setSelected(new Set())}
                      className="ml-auto text-slate-400 hover:text-white text-xs"
                      data-testid="bulk-clear">
                Clear selection
              </button>
            </div>
          )}

          {/* Table */}
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                      {canBulk && (
                        <TableHead className="w-10">
                          <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                                    data-testid="bulk-select-all" />
                        </TableHead>
                      )}
                      <SortHead col="name" filters={filters} onSort={toggleSort} />
                      <TableHead>Tags</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <SortHead col="ticket_count" filters={filters} onSort={toggleSort} className="text-right" />
                      <SortHead col="last_ticket_at" filters={filters} onSort={toggleSort} />
                      <SortHead col="csat_avg" filters={filters} onSort={toggleSort} className="text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && contacts.length === 0 && (
                      <TableRow><TableCell colSpan={canBulk ? 8 : 7} className="text-center text-sm text-slate-400 py-12">Loading…</TableCell></TableRow>
                    )}
                    {!loading && contacts.length === 0 && (
                      <TableRow><TableCell colSpan={canBulk ? 8 : 7} className="text-center text-sm text-slate-400 py-12">No customers match these filters.</TableCell></TableRow>
                    )}
                    {contacts.map((c) => (
                      <TableRow key={c.id}
                                className={`hover:bg-slate-50 ${selected.has(c.id) ? "bg-indigo-50/40" : ""}`}
                                data-testid={`customer-row-${c.id}`}>
                        {canBulk && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)}
                                      data-testid={`row-checkbox-${c.id}`} />
                          </TableCell>
                        )}
                        <TableCell className="font-medium text-sm text-slate-800 cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          <Link to={`/customers/${c.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-indigo-700">{c.name}</Link>
                          <div className="text-[10px] text-slate-400">{c.language}</div>
                        </TableCell>
                        <TableCell className="cursor-pointer max-w-[180px]" onClick={() => navigate(`/customers/${c.id}`)}>
                          <TagsCell tags={c.tags || []} />
                        </TableCell>
                        <TableCell className="text-sm font-mono text-slate-700 cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          {c.phone_e164 || c.phone || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[180px] truncate cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          {c.email || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-right cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          <span className="font-semibold text-slate-700">{c.ticket_count}</span>
                          {c.open_count > 0 && (
                            <Badge variant="outline" className="ml-1.5 bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{c.open_count} open</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          {c.last_ticket_at ? new Date(c.last_ticket_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-sm text-right cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                          {c.csat_avg != null ? (
                            <span className="text-emerald-700 font-semibold">{c.csat_avg}/5</span>
                          ) : <span className="text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="pagination-status">
              Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="page-prev">
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total}
                      onClick={() => setPage((p) => p + 1)} data-testid="page-next">
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddCustomerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        countryCodes={countryCodes}
        onCreated={load}
      />

      <BulkTagDialog
        mode={bulkTagDialog}
        onClose={() => setBulkTagDialog(null)}
        selectedIds={Array.from(selected)}
        onDone={load}
      />
    </div>
  );
}
